import { apiRequest } from "@/lib/queryClient";

const VAPID_PUBLIC_KEY = import.meta.env.VITE_VAPID_PUBLIC_KEY as string | undefined;

export function isPushSupported(): boolean {
  return "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;
}

export function isStandalone(): boolean {
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    // iOS Safari legacy flag
    (navigator as any).standalone === true
  );
}

export function isIos(): boolean {
  return /iP(hone|ad|od)/.test(navigator.userAgent) ||
    // iPadOS 13+ reports as Mac but has touch
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
}

export function registerServiceWorker(): void {
  if (!("serviceWorker" in navigator)) return;
  navigator.serviceWorker.register("/sw.js").catch((err) => {
    console.warn("[WebPush] Service worker registration failed:", err);
  });
}

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; i++) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

export async function getExistingSubscription(): Promise<PushSubscription | null> {
  if (!isPushSupported()) return null;
  try {
    const registration = await navigator.serviceWorker.ready;
    return await registration.pushManager.getSubscription();
  } catch {
    return null;
  }
}

// Requests permission if needed, subscribes the browser, and registers the
// subscription with the server. Returns the resulting permission state.
export async function enablePush(): Promise<NotificationPermission | "unsupported" | "unconfigured"> {
  if (!isPushSupported()) return "unsupported";
  if (!VAPID_PUBLIC_KEY) {
    console.warn("[WebPush] VITE_VAPID_PUBLIC_KEY is not set; push disabled");
    return "unconfigured";
  }

  const permission = await Notification.requestPermission();
  if (permission !== "granted") return permission;

  const registration = await navigator.serviceWorker.ready;
  const subscription =
    (await registration.pushManager.getSubscription()) ||
    (await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
    }));

  try {
    await apiRequest("POST", "/api/push/subscribe", subscription.toJSON());
  } catch (err) {
    // The device subscribed but the server refused to store it. Left alone,
    // every "is this device subscribed" check would read the orphaned browser
    // subscription as ON while the server sends nothing. Roll the browser back
    // so the switch tells the truth.
    await subscription.unsubscribe().catch(() => {});
    throw err;
  }
  return "granted";
}

export async function disablePush(): Promise<void> {
  const subscription = await getExistingSubscription();
  if (!subscription) return;
  const endpoint = subscription.endpoint;
  await subscription.unsubscribe();
  await apiRequest("DELETE", "/api/push/subscribe", { endpoint }).catch(() => {});
}
